<template>
  <ListDropdown
    ref="dropdownElement"
    v-model="selectedCategories"
    multiple
    class="product-category-dropdown"
    placeholder="Kategorien"
    label="Kategorie wählen"
    include-blank="Alle Kategorien"
    :item-url="itemUrl"
    :options="options"
    :disabled="isDisabled"
    @change="change"
  />
</template>

<script setup lang="ts">
import { type Dropdown, ListDropdown } from '@i22/td-longtail-vue-components';
import { withDefaults, computed, ref, watch } from 'vue';
import type { OfferProductCategoryAggregation } from '~/lib/goliath/goliath.types';
import traverseAggregations from '~/lib/aggregation-utils/traverse';

export type ProductCategoryProps = {
  value?: string[];
  aggregations?: OfferProductCategoryAggregation[];
  itemUrl?: (value: any) => string;
  disabled?: boolean;
  tree?: boolean;
};

const props = withDefaults(defineProps<ProductCategoryProps>(), {
  value: undefined,
  aggregations: undefined,
  itemUrl: undefined,
});

const emits = defineEmits<{
  (e: 'change'): void;
  (e: 'input', categories: string[]): void;
}>();

const selectedCategories = ref<string[]>(props.value);
const dropdownElement = ref<typeof Dropdown | null>(null);

const rootAggregation = computed(() => props.aggregations.find((agg) => agg.selected));

const rootCategoryPath = computed(() => rootAggregation.value?.productCategory?.path);

const optionAggregations = computed(() =>
  props.tree ? traverseAggregations(props.aggregations) : rootAggregation.value?.children || []
);

const options = computed(() => {
  if ((optionAggregations.value?.length || 0) === 0) return [];
  return optionAggregations.value.map((aggregation) => ({
    label: aggregation.productCategory.name,
    value: aggregation.productCategory.path,
    count: aggregation.offersCount,
    depth: props.tree ? aggregation.productCategory.depth : 0,
  }));
});

const isDisabled = computed(() => props.disabled || options.value.length === 0);

watch(
  () => props.value,
  (val) => {
    selectedCategories.value = val;
  }
);

const change = () => {
  // We have multiple parent-categories
  if (props.tree) {
    emits('input', selectedCategories.value);
  } else {
    // remove root category, if selected
    const filtered = selectedCategories.value.filter((value) => value !== rootCategoryPath.value);

    // add root category if no category is selected
    if (filtered.length === 0 && rootCategoryPath.value) {
      filtered.push(rootCategoryPath.value);
    }
    emits('input', filtered);
  }
  emits('change');
};
</script>
